import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import sigmaLogo from '../Images/sigma-bank-logo.png';

/**
 * Catch-all 404 screen.
 *
 * App.js previously had no `*` route, so any unmatched path rendered an empty
 * page with only the bottom nav — no message, no way back. That was reachable
 * in normal use: a successful password reset navigated to `/sign-In`, which
 * React Router treats as distinct from `/sign-in`.
 *
 * The "go back" target follows who is actually signed in, so an admin is never
 * sent to a mobile-only user route.
 */
const NotFound = () => {
    const { user } = useAuth();
    const location = useLocation();

    const role = user?.user_metadata?.role || localStorage.getItem('role');
    const isAdmin = String(role) === '4';

    const homePath = !user ? '/sign-in' : isAdmin ? '/admin' : '/welcome';
    const homeLabel = !user ? 'Go to Sign In' : isAdmin ? 'Go to Dashboard' : 'Go to My Account';

    return (
        <div style={styles.wrapper}>
            <div style={styles.orb1}></div>
            <div style={styles.orb2}></div>

            <div style={styles.card}>
                <div style={styles.logoCircle}>
                    <img src={sigmaLogo} alt="Sigma Bank" style={styles.logoImage} />
                </div>

                <div style={styles.brandRow}>
                    <div style={styles.brandDot}></div>
                    <span style={styles.brandName}>Sigma Bank</span>
                </div>

                <div style={styles.code}>404</div>
                <h1 style={styles.title}>Page not found</h1>
                <p style={styles.message}>
                    We couldn&apos;t find a page at{' '}
                    <span style={styles.path}>{location.pathname}</span>.
                    It may have been moved, or the link may be incorrect.
                </p>

                <div style={styles.divider}></div>

                <Link to={homePath} style={styles.primaryBtn}>
                    <i className='bx bx-home-alt' style={styles.btnIcon}></i>
                    {homeLabel}
                </Link>

                <p style={styles.help}>
                    Need a hand? <a href="mailto:support@sigmabank.ai" style={styles.link}>support@sigmabank.ai</a>
                </p>
            </div>
        </div>
    );
};

const styles = {
    wrapper: {
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(160deg, #060320 0%, #0e0758 30%, #1a0d4d 60%, #0a0630 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
    },
    orb1: {
        position: 'absolute',
        top: '-120px',
        right: '-100px',
        width: '360px',
        height: '360px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(25,191,177,0.12) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    orb2: {
        position: 'absolute',
        bottom: '-140px',
        left: '-120px',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(14,7,88,0.35) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    card: {
        position: 'relative',
        zIndex: 1,
        width: '100%',
        maxWidth: '420px',
        background: 'rgba(255, 255, 255, 0.04)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '20px',
        padding: '36px 28px',
        textAlign: 'center',
        backdropFilter: 'blur(12px)',
    },
    logoCircle: {
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        background: '#FFFFFF',
        margin: '0 auto 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
    },
    logoImage: { width: '44px', height: '44px', objectFit: 'contain' },
    brandRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        marginBottom: '20px',
    },
    brandDot: { width: '6px', height: '6px', borderRadius: '50%', background: '#19bfb1' },
    brandName: {
        color: 'rgba(255, 255, 255, 0.5)',
        fontSize: '13px',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
    },
    code: {
        fontSize: '54px',
        fontWeight: 700,
        lineHeight: 1,
        color: '#19bfb1',
        letterSpacing: '0.02em',
        marginBottom: '10px',
    },
    title: { fontSize: '22px', fontWeight: 600, color: '#ffffff', margin: '0 0 10px' },
    message: {
        fontSize: '14px',
        lineHeight: 1.6,
        color: 'rgba(255, 255, 255, 0.6)',
        margin: '0 0 4px',
        wordBreak: 'break-word',
    },
    path: { color: 'rgba(255,255,255,0.85)', fontFamily: 'monospace', fontSize: '13px' },
    divider: {
        height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(25,191,177,0.4), transparent)',
        margin: '24px 0',
    },
    primaryBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        width: '100%',
        padding: '13px 20px',
        borderRadius: '12px',
        background: '#19bfb1',
        color: '#04121a',
        fontSize: '15px',
        fontWeight: 600,
        textDecoration: 'none',
    },
    btnIcon: { fontSize: '18px' },
    help: { marginTop: '18px', marginBottom: 0, fontSize: '13px', color: 'rgba(255,255,255,0.35)' },
    link: { color: '#19bfb1', textDecoration: 'none' },
};

export default NotFound;
